import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { consume } from '@lit/context'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct, Option, ProductModel, Variant, VariantAttribute } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { partsStyle } from 'components/discovery/product/tile-parts/tile-parts.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { map } from 'lit/directives/map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { hasVariantList } from 'components/shared/variants/variant-grouping.js'


@customElement('csc-product-tile-variants')
export class CSCProductTileVariants extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, partsStyle]

  @consume({ context: productTileProductContext })
  @state()
  product: InternalProduct = new ProductModel().parseToInternal()

  @property({ type: Number, attribute: 'display-limit' })
  displayLimit: number = 5

  /** @deprecated Use `display-limit` instead. */
  @property({ type: Number, attribute: 'max-variants-amount' })
  set maxVariantsAmount(value: number) {
    console.warn('<csc-product-tile-variants> "max-variants-amount" is deprecated, use "display-limit" instead.')
    this.displayLimit = value
  }

  get maxVariantsAmount() {
    return this.displayLimit
  }

  @inject(WINDOW_LOCATION)
  windowLocation!: WindowLocation

  groupAttributesByDisplayMode(variants: Variant[]) {
    const displayModesMap: Map<string, Option[]>= new Map()

    variants.forEach((el: Variant, index: number) => {
      el.variant.attributes.forEach((attribute: VariantAttribute) => {
        const options = displayModesMap.get(`${attribute.displayMode}_${attribute.name}`) || []
        options.push({
          ...attribute,
          pageLink: el.pageLink,
          selected: index === 0,
          displayMode: attribute.displayMode
        })
        displayModesMap.set(`${attribute.displayMode}_${attribute.name}`, options)
      })
    })

    return displayModesMap
  }

  mapVariantTypeToVariantSwatch(type: string): string {
    const variantSwatchMap: { [key: string]: string } = {
      'property_color': 'color',
      'dropdown': 'text',
      'product_image': 'image',
      'property_image': 'image',
    }

    return variantSwatchMap[type] || ''
  }

  override render() {
    if (!hasVariantList(this.product)) {
      return nothing
    } else {
      const modifiedVariants = this.groupAttributesByDisplayMode(this.product.variants)

      const modifiedVariantsValue: Array<Option> = modifiedVariants.values().next().value ?? []

      const swatchVariant = modifiedVariants.size === 1 && modifiedVariantsValue.length && modifiedVariantsValue[0] !== undefined
        ? this.mapVariantTypeToVariantSwatch(modifiedVariantsValue[0].displayMode)
        : null

      return html`
        ${modifiedVariants.size > 1 ? html`
          <cse-text
            variant="caption-l"
            class="csc-product-tile-variants"
          >
            ${t('variants', { ns: 'csc-product-tile', default: 'Multiple variants available' })}
          </cse-text>
        ` :
        html `
        <cse-group class="variant-group csc-product-tile-variants" column-gap="05">
          ${map(modifiedVariantsValue.slice(0, this.displayLimit), (option: Option) => html`
            <cse-link
              url="${option.pageLink}"
              variant="secondary"
              font-variant="product-title-m"
            >
              <cse-swatch
                variant="${swatchVariant}"
                size="small"
                color="${option.cssColor}"
                tooltip="${option.name}"
                image-source="${option.picture}"
                ?selected="${option.selected}"
                ?disabled="${!option.pageLink}"
              >${option.value}</cse-swatch>
            </cse-link>
          `)}
          ${this.displayLimit && modifiedVariantsValue.length > this.displayLimit ? html`
            <cse-link url="${this.product.pageLink}"> + ${modifiedVariantsValue.length - this.displayLimit}</cse-link>`
            : nothing}

        </cse-group>
        `}
      `
    }
  }
}
